import { ReactNode, useId } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Switch } from "@design-system/Switch/Switch";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    ReportSettingSpotExplanation,
    ReportSettingsSpotExplanation,
} from "@common/helpers/report-settings/explain-report-setting-spot";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ReportSettingValues } from "@common/helpers/report-settings/report-setting-fields";
import styles from "../ReportSettings.module.scss";
import { UpdateReportSetting } from "../layout/reportSettingsDrafts";
import { ResetToInheritedButton } from "../ResetToInheritedButton";
import { TemplateNames } from "../reportSettingsSourceCopy";
import { SelectOption } from "./numberFormatControls";

type NumberFormatKey = keyof NumberFormatValues;
type ToggleKey = "isNegativeInRed" | "hasThousandsSeparator";
type SelectKey = Exclude<NumberFormatKey, ToggleKey>;

interface NumberFormatRowContext {
    view: ReportSettingsSpotExplanation;
    templateNames: TemplateNames;
    canEdit: boolean;
    updateField: UpdateReportSetting;
}

interface NumberFormatSelectRowProps<
    K extends SelectKey,
> extends NumberFormatRowContext {
    label: string;
    field: K;
    options: Array<SelectOption<ReportSettingValues[K]>>;
    description?: string;
}

interface NumberFormatSwitchRowProps extends NumberFormatRowContext {
    label: string;
    field: ToggleKey;
}

interface NumberFormatRowShellProps {
    label: string;
    description?: string;
    labelId?: string;
    reset: ReactNode;
    children: ReactNode;
}

const NumberFormatRowShell = ({
    label,
    description,
    labelId,
    reset,
    children,
}: Readonly<NumberFormatRowShellProps>) => (
    <HStack justify="between" align="center" gap={16}>
        <VStack gap={4} className={styles.rowLabel}>
            <span id={labelId} className={styles.sectionLabel}>
                {label}
            </span>
            {description && (
                <span className={styles.inheritedHint}>{description}</span>
            )}
        </VStack>
        <HStack gap={8} align="center">
            {children}
            <div className={styles.resetSlot}>{reset}</div>
        </HStack>
    </HStack>
);

export const NumberFormatSelectRow = <K extends SelectKey>({
    view,
    templateNames,
    canEdit,
    updateField,
    label,
    field,
    options,
    description,
}: Readonly<NumberFormatSelectRowProps<K>>) => {
    const labelId = useId();
    const explanations: {
        [P in SelectKey]: ReportSettingSpotExplanation<P>;
    } = view;
    const explanation = explanations[field];
    return (
        <NumberFormatRowShell
            label={label}
            description={description}
            labelId={labelId}
            reset={
                <ResetToInheritedButton
                    field={field}
                    explanation={explanation}
                    templateNames={templateNames}
                    canEdit={canEdit}
                    onReset={() => updateField(field, null)}
                />
            }
        >
            <Picker.Select<ReportSettingValues[K]>
                items={options}
                value={explanation.effective.value}
                onValueChange={(nextValue) => {
                    if (nextValue !== null) {
                        updateField(field, nextValue);
                    }
                }}
                disabled={!canEdit}
                trigger={
                    <Picker.Button
                        variant="primary"
                        aria-labelledby={labelId}
                    />
                }
            />
        </NumberFormatRowShell>
    );
};

export const NumberFormatSwitchRow = ({
    view,
    templateNames,
    canEdit,
    updateField,
    label,
    field,
}: Readonly<NumberFormatSwitchRowProps>) => {
    const labelId = useId();
    const explanation = view[field];
    return (
        <NumberFormatRowShell
            label={label}
            labelId={labelId}
            reset={
                <ResetToInheritedButton
                    field={field}
                    explanation={explanation}
                    templateNames={templateNames}
                    canEdit={canEdit}
                    onReset={() => updateField(field, null)}
                />
            }
        >
            <Switch
                checked={explanation.effective.value}
                onCheckedChange={(checked) => updateField(field, checked)}
                disabled={!canEdit}
                aria-labelledby={labelId}
            />
        </NumberFormatRowShell>
    );
};
